import { useCallback, useEffect, useState } from 'react';
import { Col } from '@amzn/stencil-react-components/layout';
import { Input } from '@amzn/stencil-react-components/input';

import { ActionModal } from '../../../shared/ui/ActionModal';
import { RequiredLabel } from '../../../shared/ui/forms/RequiredLabel';
import { FieldErrorText } from '../../../shared/ui/forms/FieldErrorText';
import {
  isValidDocLinkUrl,
  isDuplicateDocLinkUrl,
} from '../../../shared/ui/forms/docLinkValidation';
import type { DocLinkItem } from '../../../shared/ui/forms/DocLinksField';

/** Visible copy — supplied by the caller so this stays i18n-bundle-agnostic. */
export interface AddDocLinkModalLabels {
  readonly title: string;
  readonly documentLabel: string;
  readonly documentPlaceholder: string;
  readonly urlLabel: string;
  readonly urlPlaceholder: string;
  readonly addButton: string;
  readonly cancelButton: string;
  readonly invalidUrlText: string;
  readonly duplicateUrlText: string;
}

/** Builds a native `data-testid` prop object (or nothing when the id is
 *  undefined), so the attribute name is written once. RTL's getByTestId
 *  resolves `data-testid`, unlike Stencil's dashed `data-test-id`. */
function testIdProp(id: string | undefined): Record<string, string> {
  return id !== undefined ? { 'data-testid': id } : {};
}

export interface AddDocLinkModalProps {
  readonly isOpen: boolean;
  /** Current list — used to reject a URL that is already present. */
  readonly existingLinks: readonly DocLinkItem[];
  readonly labels: AddDocLinkModalLabels;
  /** Appends the new link to the caller's list. */
  readonly onAdd: (link: DocLinkItem) => void;
  readonly onClose: () => void;
  readonly dataTestId?: string;
}

/**
 * "Add Document Link" modal — the Stencil/v2 port of the manual workflow's SOP
 * document-link modal. Collects a required Document Title and a required,
 * validated URL, then appends `{ title, link }` to the caller's list and
 * closes. Matches V1 parity: both fields are required and Add stays disabled
 * until the title is non-empty and the URL is a valid, non-duplicate http(s)
 * link.
 *
 * Composes the shared {@link ActionModal} shell; URL validation and the
 * duplicate check come from the shared `docLinkValidation` module (same logic
 * the links table uses to decide what renders as a clickable href).
 */
export function AddDocLinkModal({
  isOpen,
  existingLinks,
  labels,
  onAdd,
  onClose,
  dataTestId,
}: AddDocLinkModalProps): JSX.Element {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');

  // Reset the fields whenever the modal transitions to open so a prior
  // entry never bleeds into the next one.
  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
      setTitle('');
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
      setUrl('');
    }
  }, [isOpen]);

  const trimmedTitle = title.trim();
  const trimmedUrl = url.trim();
  const urlLooksInvalid = trimmedUrl.length > 0 && !isValidDocLinkUrl(trimmedUrl);
  const isDuplicate =
    isValidDocLinkUrl(trimmedUrl) && isDuplicateDocLinkUrl(existingLinks, trimmedUrl);
  // V1 parity: both the title and a valid, non-duplicate URL are required.
  const canAdd = trimmedTitle.length > 0 && isValidDocLinkUrl(trimmedUrl) && !isDuplicate;

  // Derived (not stored) so the error never outlives the input that caused it:
  // invalid URL error wins over the duplicate error; neither shows while empty.
  const urlError = urlLooksInvalid
    ? labels.invalidUrlText
    : isDuplicate
      ? labels.duplicateUrlText
      : undefined;

  const handleConfirm = useCallback(() => {
    if (!canAdd) return;
    onAdd({ title: trimmedTitle, link: trimmedUrl });
    onClose();
  }, [canAdd, onAdd, onClose, trimmedTitle, trimmedUrl]);

  const titleTestId = dataTestId ? `${dataTestId}-title-input` : undefined;
  const urlTestId = dataTestId ? `${dataTestId}-url-input` : undefined;
  const urlErrorTestId = dataTestId ? `${dataTestId}-url-error` : undefined;

  return (
    <ActionModal
      isOpen={isOpen}
      titleText={labels.title}
      isProcessing={false}
      confirmLabel={labels.addButton}
      processingLabel={labels.addButton}
      closeLabel={labels.cancelButton}
      onClose={onClose}
      onConfirm={handleConfirm}
      {...(dataTestId ? { confirmTestId: `${dataTestId}-confirm` } : {})}
      {...(dataTestId ? { closeTestId: `${dataTestId}-cancel` } : {})}
      confirmDisabled={!canAdd}
    >
      <Col gridGap="dimensions.spacing.300" width="100%">
        <Col gridGap="dimensions.spacing.100">
          <RequiredLabel isRequired hasError={false}>
            {labels.documentLabel}
          </RequiredLabel>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={labels.documentPlaceholder}
            aria-label={labels.documentLabel}
            aria-required
            {...testIdProp(titleTestId)}
          />
        </Col>
        <Col gridGap="dimensions.spacing.100">
          <RequiredLabel isRequired hasError={urlError !== undefined}>
            {labels.urlLabel}
          </RequiredLabel>
          <Input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={labels.urlPlaceholder}
            aria-label={labels.urlLabel}
            aria-required
            aria-invalid={urlError !== undefined}
            {...testIdProp(urlTestId)}
          />
          {urlError !== undefined ? (
            <FieldErrorText {...(urlErrorTestId ? { dataTestId: urlErrorTestId } : {})}>
              {urlError}
            </FieldErrorText>
          ) : null}
        </Col>
      </Col>
    </ActionModal>
  );
}
